import { useCallback, useMemo } from "react";
import { shallowEqual } from "react-redux";
import { t } from "ttag";
import _ from "underscore";

import { getErrorMessage } from "metabase/api/utils/errors";
import { useToast } from "metabase/common/hooks/use-toast";
import { useSelector } from "metabase/redux";
import type {
  EnterpriseSettingKey,
  EnterpriseSettingValue,
  EnterpriseSettings,
  SettingDefinition,
} from "metabase-types/api";

import {
  useGetAdminSettingsDetailsQuery,
  useGetSettingsQuery,
  useUpdateSettingMutation,
  useUpdateSettingsMutation,
} from "./api";
import { getSetting, getSettings } from "./selectors";

/**
 * One hook to get setting values and mutators for a given setting
 */
export const useAdminSetting = <SettingName extends EnterpriseSettingKey>(
  settingName: SettingName,
) => {
  const {
    isLoading: settingsLoading,
    isFetching: settingsFetching,
    ...apiProps
  } = useGetSettingsQuery();
  const {
    data: settingsDetails,
    isLoading: detailsLoading,
    isFetching: detailsFetching,
  } = useGetAdminSettingsDetailsQuery();
  const [updateSetting, updateSettingResult] = useUpdateSettingMutation();
  const [updateSettings, updateSettingsResult] = useUpdateSettingsMutation();

  const settingDetails = settingsDetails?.[settingName];

  const [sendToast] = useToast();

  const handleUpdateSetting = useCallback(
    async <K extends EnterpriseSettingKey>({
      key,
      value,
      toast = true,
    }: {
      key: K;
      value: EnterpriseSettingValue<K>;
      toast?: boolean;
    }) => {
      const response = await updateSetting({ key, value });

      if (!toast) {
        return response;
      }

      if (response.error) {
        const message = getErrorMessage(response.error, t`Error saving ${key}`);

        sendToast({
          message,
          icon: "warning",
          toastColor: "feedback-negative",
        });
      } else {
        sendToast({ message: t`Changes saved` });
      }
      return response;
    },
    [updateSetting, sendToast],
  );

  const handleUpdateSettings = useCallback(
    async ({
      toast = true,
      ...settings
    }: {
      toast?: boolean;
    } & Partial<EnterpriseSettings>) => {
      const response = await updateSettings(settings);

      if (!toast) {
        return response;
      }

      if (response.error) {
        const message = getErrorMessage(
          response.error,
          t`Error saving settings`,
        );

        sendToast({
          message,
          icon: "warning",
          toastColor: "feedback-negative",
        });
      } else {
        sendToast({ message: t`Changes saved`, icon: "check_filled" });
      }
      return response;
    },
    [updateSettings, sendToast],
  );

  // Read the value through `getSetting` so it resolves from the cache with a
  // synchronous fallback to the bootstrap (available before the fetch resolves),
  // the same as `useSetting`.
  const settingValue = useSelector((state) => getSetting(state, settingName));

  return {
    value: settingValue,
    settingDetails,
    description: settingDetails?.description,
    updateSetting: handleUpdateSetting,
    updateSettings: handleUpdateSettings,
    updateSettingResult,
    updateSettingsResult,
    isLoading: settingsLoading || detailsLoading,
    isFetching: settingsFetching || detailsFetching,
    ...apiProps,
  };
};

/**
 * Hook to get setting values and mutators for multiple settings
 */
export const useAdminSettings = <
  SettingNames extends readonly EnterpriseSettingKey[],
>(
  settingNames: SettingNames,
) => {
  const {
    data: settings,
    isLoading: settingsLoading,
    ...apiProps
  } = useGetSettingsQuery();
  const { data: settingsDetails, isLoading: detailsLoading } =
    useGetAdminSettingsDetailsQuery();
  const [updateSettings, updateSettingsResult] = useUpdateSettingsMutation();

  const [sendToast] = useToast();

  const handleUpdateSettings = useCallback(
    async ({
      toast = true,
      ...settings
    }: { toast?: boolean } & Partial<EnterpriseSettings>) => {
      const response = await updateSettings(settings);

      if (toast) {
        if (response.error) {
          const message = getErrorMessage(
            response.error,
            t`Error saving settings`,
          );

          sendToast({
            message,
            icon: "warning",
            toastColor: "feedback-negative",
          });
        } else {
          sendToast({ message: t`Changes saved`, icon: "check_filled" });
        }
      }

      return response;
    },
    [updateSettings, sendToast],
  );

  type Values = { [K in SettingNames[number]]: EnterpriseSettings[K] };
  const values = useSelector(
    // Unjustified type cast. FIXME
    (state) => _.pick(getSettings(state), ...settingNames) as Values,
    shallowEqual,
  );

  type Details = { [K in SettingNames[number]]: SettingDefinition<K> };
  const details = useMemo(() => {
    // Unjustified type cast. FIXME
    return (
      settingsDetails ? _.pick(settingsDetails, ...settingNames) : {}
    ) as Details;
  }, [settingsDetails, settingNames]);

  return {
    values,
    details,
    updateSettings: handleUpdateSettings,
    updateSettingsResult,
    isLoading: settingsLoading || detailsLoading,
    ...apiProps,
  };
};
